import { Customer } from "@/types/customer";
import app_config from "../app_config.json";

export enum CartItemType {
  iPhone = 3,
  Smartphone = 11,
  iPad = 4,
  Laptop = 1,
  Tablet = 2,
  GamingSystem = 7,
  Camera_iPod = 8,
  Apple_watch = 9,
  Smartwatch = 10,
  eReader = 6,
}

export const CartItemCode = {
  iphone: { name: "iPhone", type: CartItemType.iPhone, code: "iphone" },
  smartphone: {
    name: "Smartphone",
    type: CartItemType.Smartphone,
    code: "smartphone",
  },
  ipad: { name: "iPad", type: CartItemType.iPad, code: "ipad" },
  laptop: { name: "Laptop", type: CartItemType.Laptop, code: "laptop" },
  tablet: { name: "Tablet", type: CartItemType.Tablet, code: "tablet" },
  gamingsystem: {
    name: "Gaming System",
    type: CartItemType.GamingSystem,
    code: "gamingsystem",
  },
  camera: {
    name: "Camera / iPod",
    type: CartItemType.Camera_iPod,
    code: "camera",
  },
  applewatch: {
    name: "Apple Watch",
    type: CartItemType.Apple_watch,
    code: "applewatch",
  },
  smartwatch: {
    name: "Smartwatch",
    type: CartItemType.Smartwatch,
    code: "smartwatch",
  },
  ereader: { name: "eReader", type: CartItemType.eReader, code: "ereader" },
};

export enum CartItemPaymentTerms {
  undefiend = 0,
  Monthly = 1,
  InFull = 2,
  AnnualAutoRenew = 3,
}

export interface DeductibleObject {
  text: string;
  value: number;
}

/**
 * This class is the basic cart item.
 */
export class CartItem {
  // Members
  code: string;
  type: CartItemType;
  name: string;
  deductible: number;
  deductible_object: DeductibleObject;
  customer: Customer;
  manufacturer: string;
  model: string;
  serial_number: string;
  id: number;
  match_with_first: boolean;
  discount: number;
  sku: string;
  processing_fee_sku: string;
  installmentPremium: number;
  policy_term = 0;
  policy_term_text: string;
  coverage = 0;
  payment_terms: CartItemPaymentTerms;
  due_today = 0;
  processing_fee = 0;
  is_non_renew = true;
  static total_count: number = 0;
  coverage_type: number;
  //quoteId: number;

  /**
   * Default constructor.
   * @param code
   */
  constructor(code: string) {
    this.code = code;
    this.id = Date.now() + ++CartItem.total_count;
    this.type = CartItemCode[code].type;
    this.name = CartItemCode[code].name;
    this.coverage = 0;
    this.deductible = -1;
    this.deductible_object = { text: "", value: 0 };
    this.policy_term = 0;
    this.policy_term_text = "";
    this.payment_terms = 0;
    this.customer = new Customer();
    this.manufacturer = "";
    this.model = "";
    this.serial_number = "";
    this.discount = 0;
    this.match_with_first = false;
    this.sku = "";
    this.processing_fee_sku = "";
    this.installmentPremium = 0;
    this.coverage_type = 0;
    //this.quoteId = 0;
  }

  getPersistObject() {
    return {
      // Members
      code: this.code,
      type: this.type,
      name: this.name,
      deductible: this.deductible,
      deductible_object: this.deductible_object,
      customer: this.customer,
      manufacturer: this.manufacturer,
      model: this.model,
      serial_number: this.serial_number,
      id: this.id,
      match_with_first: this.match_with_first,
      discount: this.discount,
      sku: this.sku,
      processing_fee_sku: this.processing_fee_sku,
      installmentPremium: this.installmentPremium,
      policy_term: this.policy_term,
      policy_term_text: this.policy_term_text,
      coverage: this.coverage,
      payment_terms: this.payment_terms,
      due_today: this.due_today,
      processing_fee: this.processing_fee,
      is_non_renew: this.is_non_renew,
      coverage_type: this.coverage_type,
      //quoteId: this.quoteId,
    };
  }

  fromPersistObject(obj: any) {
    this.code = obj.code;
    this.id = obj.id;
    this.type = obj.type;
    this.name = obj.name;
    this.coverage = obj.coverage;
    this.deductible = obj.deductible;
    this.deductible_object = obj.deductible_object;
    this.policy_term = obj.policy_term;
    this.policy_term_text = obj.policy_term_text;
    this.payment_terms = obj.payment_terms;
    this.customer = { ...obj.customer };
    this.manufacturer = obj.manufacturer;
    this.model = obj.model;
    this.serial_number = obj.serial_number;
    this.discount = obj.discount;
    this.match_with_first = obj.match_with_first;
    this.sku = obj.sku;
    this.processing_fee_sku = obj.processing_fee_sku;
    this.installmentPremium = obj.installmentPremium;
    this.due_today = obj.due_today;
    this.processing_fee = obj.processing_fee;
    this.is_non_renew = obj.is_non_renew;
    this.coverage_type = obj.coverage_type;
    //this.quoteId = obj.quoteId;
  }

  get policyTermName() {
    switch (this.payment_terms) {
      case CartItemPaymentTerms.Monthly:
        return "Monthly Billing";
      case CartItemPaymentTerms.InFull:
        return "Paid In Full";
      case CartItemPaymentTerms.AnnualAutoRenew:
        return "Annual Billing";
      default:
        return "";
    }
  }
  get policyShortTermName() {
    switch (this.payment_terms) {
      case CartItemPaymentTerms.Monthly:
        return "Monthly";
      case CartItemPaymentTerms.InFull:
        return "In Full";
      case CartItemPaymentTerms.AnnualAutoRenew:
        return "Annual Auto Renew";
      default:
        return "";
    }
  }

  get policyTermNameNew(): string {
    switch (this.payment_terms) {
      case CartItemPaymentTerms.Monthly:
        return "monthly";
      case CartItemPaymentTerms.InFull:
        return "yearly";
      case CartItemPaymentTerms.AnnualAutoRenew:
        return "yearly-renew";
      default:
        return "";
    }
  }

  get policyWagType() {
    switch (this.payment_terms) {
      case CartItemPaymentTerms.Monthly:
        return "Installment";
      case CartItemPaymentTerms.InFull:
        return "Annual";
      case CartItemPaymentTerms.AnnualAutoRenew:
        return "Annual AutoRenew";
      default:
        return "";
    }
  }

  get effectiveDateString() {
    const date = this.effectiveDate;
    const day = date.getDate();
    const month = date.getMonth() + 1;
    const year = date.getFullYear();
    return month + "/" + day + "/" + year;
  }

  get effectiveDate() {
    const currentDate = new Date();
    currentDate.setDate(currentDate.getDate() + 1);
    return currentDate;
  }

  get total() {
    if (this.payment_terms != CartItemPaymentTerms.Monthly) {
      return this.due_today;
    }
    return this.due_today + app_config.wag_api.order.monthly_processing_fee;
  }
}
